<template>
  <div class="feature-detail-page">
    <landing-header />

    <main>
      <section class="hero-feature">
        <div class="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-center gap-12">
          <div class="lg:w-1/2">
            <span class="badge-category">Module Opérationnel</span>
            <h1 class="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Suivi des interventions : <br />
              <span class="gradient-text">Pilotez du terrain au bureau</span>
            </h1>
            <p class="text-xl text-gray-600 mb-8">
              Termoflow centralise chaque information pour garantir une exécution maîtrisée, une traçabilité sans faille et une visibilité permanente sur votre activité CVC.
            </p>
            <div class="flex flex-wrap gap-4">
              <a href="/contact"><button class="btn-primary">Demander une démonstration</button></a>
              <a href="#details" class="btn-secondary">Explorer le module</a>
            </div>
          </div>
          <div class="lg:w-1/2 w-full">
            <div class="hero-mockup shadow-2xl">
              <div class="mockup-header">
                <div class="dot red"></div><div class="dot orange"></div><div class="dot green"></div>
              </div>
              <div class="mockup-body">
                <div class="skeleton-line title"></div>
                <div class="status-grid">
                  <div class="status-item active">En cours</div>
                  <div class="status-item">Terminée</div>
                  <div class="status-item wait">Attente pièce</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="details" class="py-20 bg-gray-900 text-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="text-center mb-16">
            <h2 class="text-3xl font-bold mb-4">Une vision claire de l’état de vos chantiers</h2>
            <p class="text-gray-400">Ne dépendez plus des appels terrain pour savoir où en est votre activité.</p>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div v-for="status in statuses" :key="status" class="status-card">
              <i class="fa-solid fa-circle-dot text-orange-500 mb-2"></i>
              <span class="text-sm font-bold">{{ status }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4 space-y-32">
          
          <div class="flex flex-col lg:flex-row items-center gap-16">
            <div class="lg:w-1/2">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">Affectation et suivi des techniciens</h2>
              <p class="text-gray-600 mb-6">Chaque mission est associée à un expert identifié. Une organisation qui permet une meilleure répartition des charges et une responsabilité claire.</p>
              <ul class="benefit-list">
                <li><i class="fa-solid fa-check"></i> Affectation simple depuis le planning</li>
                <li><i class="fa-solid fa-check"></i> Visibilité immédiate pour le terrain</li>
                <li><i class="fa-solid fa-check"></i> Suivi de l’activité par technicien</li>
              </ul>
            </div>
            <div class="lg:w-1/2 bg-orange-50 p-8 rounded-3xl border border-orange-100">
               <i class="fa-solid fa-user-gear text-6xl text-orange-500 opacity-20"></i>
               <p class="mt-4 font-bold text-orange-900 uppercase tracking-widest text-xs">Vue Planificateur</p>
            </div>
          </div>

          <div class="flex flex-col lg:flex-row-reverse items-center gap-16">
            <div class="lg:w-1/2">
              <h2 class="text-3xl font-bold text-gray-900 mb-6">L’historique au service de la fidélisation</h2>
              <p class="text-gray-600 mb-6">Accédez à une vue consolidée de la relation client et de la santé de chaque appareil (marque, modèle, série).</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="info-card">
                  <h4 class="font-bold text-orange-600">Client</h4>
                  <p class="text-xs text-gray-500">Interventions, contrats et documents archivés.</p>
                </div>
                <div class="info-card">
                  <h4 class="font-bold text-orange-600">Appareil</h4>
                  <p class="text-xs text-gray-500">Dossier technique et CERFA liés par scan.</p>
                </div>
              </div>
            </div>
            <div class="lg:w-1/2 bg-gray-50 p-8 rounded-3xl border border-gray-200">
               <i class="fa-solid fa-microchip text-6xl text-gray-300 opacity-40"></i>
               <p class="mt-4 font-bold text-gray-400 uppercase tracking-widest text-xs">Fiche Technique Centralisée</p>
            </div>
          </div>

        </div>
      </section>

      <section class="py-24 bg-gray-50">
        <div class="max-w-7xl mx-auto px-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div class="feature-box">
              <i class="fa-solid fa-folder-tree"></i>
              <h3>Centralisation totale</h3>
              <p>Rapports, bons, contrats, CERFA, signatures client regroupés automatiquement.</p>
            </div>
            <div class="feature-box">
              <i class="fa-solid fa-mobile-screen-button"></i>
              <h3>Accès Terrain Offline</h3>
              <p>Synchronisation automatique des techniciens dès le retour du signal réseau.</p>
            </div>
            <div class="feature-box">
              <i class="fa-solid fa-magnifying-glass-chart"></i>
              <h3>Filtres Avancés</h3>
              <p>Recherchez par technicien, statut d'intervention, client, en un clic.</p>
            </div>
          </div>
        </div>
      </section>

      <section class="py-24 bg-white">
        <div class="max-w-7xl mx-auto px-4">
          <div class="bg-gradient-to-br from-orange-500 to-red-600 rounded-[3rem] p-12 text-white overflow-hidden relative">
            <div class="relative z-10 flex flex-col lg:flex-row gap-12 items-center">
              <div class="lg:w-1/2">
                <h2 class="text-3xl font-bold mb-8">Une base fiable pour votre pilotage</h2>
                <div class="space-y-4">
                  <div class="benefit-item">Meilleure visibilité sur l’activité terrain</div>
                  <div class="benefit-item">Gain de temps administratif massif</div>
                  <div class="benefit-item">Traçabilité complète et sécurisée</div>
                  <div class="benefit-item">Satisfaction client décuplée</div>
                </div>
              </div>
              <div class="lg:w-1/2 bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
                <h4 class="font-bold mb-4 uppercase text-sm tracking-widest">Impact sur l'entreprise</h4>
                <p class="text-white/80 text-sm leading-relaxed">Les données d'intervention alimentent vos statistiques, vos temps de déplacement et votre facturation sans aucune ressaisie.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="pt-10 pb-20 mb-20 text-center">
        <div class="max-w-2xl mx-auto px-4">
          <h2 class="text-3xl font-bold text-gray-900 mb-8">Prêt à structurer votre suivi ?</h2>
          <div class="flex flex-col sm:flex-row justify-center gap-4">
             <a href="/contact"><button class="btn-primary-large">Demander une démonstration</button></a>
             <router-link to="/fonctionnalites" class="btn-outline-large">Toutes les fonctionnalités</router-link>
          </div>
        </div>
      </section>

      <landing-roi />
      <landing-avis />
    </main>

    <landing-footer />
  </div>
</template>

<script>
import LandingHeader from './LandingHeader.vue'
import LandingFooter from './LandingFooter.vue'
import LandingAvis from './LandingAvis.vue'
import LandingROI from './LandingROI.vue'

export default {
  name: 'InterventionTrackingPage',
  components: {
    LandingHeader,
    LandingFooter,
    LandingAvis,
    'landing-roi': LandingROI
  },
  data() {
    return {
      statuses: [
        'À planifier',
        'Planifiée',
        'En cours',
        'Terminée',
        'Facturée',
        'Annulée'
      ]
    }
  }
}
</script>

<style scoped>
/* --- TEXT UTILS --- */
.gradient-text {
  background: linear-gradient(to right, #f97316, #ef4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* --- HERO SECTION --- */
.hero-feature {
  padding: 10rem 0 6rem;
  background: radial-gradient(circle at top right, #fff7ed, transparent 40%);
}

.badge-category {
  display: inline-block;
  padding: 0.4rem 1rem;
  background-color: #ffedd5;
  color: #c2410c;
  font-weight: 800;
  font-size: 0.75rem;
  text-transform: uppercase;
  border-radius: 9999px;
  margin-bottom: 1.5rem;
}

/* --- BUTTONS --- */
.btn-primary {
  background: linear-gradient(to right, #f97316, #ef4444);
  color: white;
  padding: 0.8rem 2rem;
  border-radius: 9999px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(249, 115, 22, 0.3);
  transition: transform 0.2s;
}

.btn-primary:hover { transform: translateY(-2px); }

.btn-secondary {
  padding: 0.8rem 2rem;
  border-radius: 9999px;
  font-weight: 700;
  color: #4b5563;
  text-decoration: none;
  border: 1px solid #e5e7eb;
}

.btn-primary-large {
  background: #f97316;
  color: white;
  padding: 1.2rem 2.5rem;
  border-radius: 9999px;
  font-weight: 800;
  font-size: 1.1rem;
  border: none;
  cursor: pointer;
}

.btn-outline-large {
  border: 2px solid #e5e7eb;
  padding: 1.2rem 2.5rem;
  border-radius: 9999px;
  font-weight: 800;
  font-size: 1.1rem;
  color: #1f2937;
  text-decoration: none;
}

/* --- MOCKUP HERO --- */
.hero-mockup {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 1.5rem;
  overflow: hidden;
  height: 350px;
}

.mockup-header {
  background: #f3f4f6;
  padding: 1rem;
  display: flex;
  gap: 0.5rem;
}

.dot { width: 10px; height: 10px; border-radius: 50%; }
.red { background: #ef4444; }
.orange { background: #f97316; }
.green { background: #22c55e; }

.mockup-body { padding: 2rem; }
.skeleton-line { background: #e5e7eb; height: 1rem; border-radius: 0.5rem; margin-bottom: 1rem; }
.title { width: 60%; height: 2rem; }

.status-grid { display: grid; gap: 1rem; margin-top: 2rem; }
.status-item { background: white; padding: 1rem; border-radius: 0.75rem; border: 1px solid #e5e7eb; font-weight: 700; font-size: 0.8rem; }
.status-item.active { border-color: #f97316; color: #f97316; }
.status-item.wait { background: #fef2f2; border-color: #fee2e2; color: #ef4444; }

/* --- CARDS & LISTS --- */
.status-card {
  background: #1f2937;
  padding: 1.5rem;
  border-radius: 1rem;
  text-align: center;
  border: 1px solid #374151;
  transition: border-color 0.3s;
}

.status-card:hover { border-color: #f97316; }

.benefit-list { list-style: none; padding: 0; }
.benefit-list li { display: flex; align-items: center; gap: 0.75rem; color: #4b5563; font-weight: 600; margin-bottom: 1rem; }
.benefit-list li i { color: #f97316; }

.info-card { background: white; padding: 1.5rem; border-radius: 1.25rem; border: 1px solid #f3f4f6; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05); }

.feature-box { background: white; padding: 2.5rem; border-radius: 2rem; border: 1px solid #f3f4f6; transition: transform 0.3s; }
.feature-box:hover { transform: translateY(-5px); }
.feature-box i { font-size: 2.5rem; color: #f97316; margin-bottom: 1.5rem; }
.feature-box h3 { font-size: 1.25rem; font-weight: 800; margin-bottom: 1rem; }
.feature-box p { color: #6b7280; font-size: 0.9rem; line-height: 1.6; }

.benefit-item { display: flex; align-items: center; font-weight: 700; font-size: 1.1rem; }

/* --- RESPONSIVE --- */
@media (max-width: 1024px) {
  .hero-feature { padding: 8rem 0 4rem; text-align: center; }
  .hero-feature .flex { flex-direction: column; }
  .hero-mockup { margin-top: 3rem; }
}
</style>